import { firstCapitalized } from './stringHelper';

/**
 * Cria a função de iniciar carregamento.
 * A função gerada tem o comportamento de incrementar em 1 o contador de carregamentos.
 */
function createStartFunction({
  loadingCountDataName,
}) {
  return function startLoading() {
    this[loadingCountDataName] += 1;
  };
}

/**
 * Cria a função de finalizar carregamento.
 * A função gerada tem o comportamento de decrementar em 1 o contador de carregamentos.
 * Também faz a checagem para garantir que o contador não fique negativo após o decremento.
 */
function createEndFunction({
  loadingCountDataName,
  clearLoadingMethodName,
}) {
  return function endLoading() {
    this[loadingCountDataName] -= 1;
    if (this[loadingCountDataName] < 0) {
      this[clearLoadingMethodName]();
    }
  };
}

/**
 * Cria a função para limpar o carregamento.
 * A função gerada tem o comportamento de zerar o contador de carregamentos.
 */
function createClearFunction({
  loadingCountDataName,
}) {
  return function clearLoading() {
    this[loadingCountDataName] = 0;
  };
}

/**
 * Cria a função para aguardar um carregamento assincrono.
 * A função gerada tem o comportamento de esperar uma promessa recebida por parametro. Enquanto
 * a promessa não acaba, ela define um carregamento, removendo ele ao fim da execução.
 */
function createWaitFunction({
  startLoadingMethodName,
  endLoadingMethodName,
}) {
  return async function waitLoading(promise) {
    this[startLoadingMethodName]();
    try {
      await promise;
    } finally {
      this[endLoadingMethodName]();
    }
  };
}

/**
 * Cria a função para gerar a computed que fala se está havendo carregamento.
 * A função gerada tem a intenção de retornar verdadeira se o contador for maior do que zero.
 */
function createLoadingComputedFunction({
  loadingCountDataName,
}) {
  return function loading() {
    return !!this[loadingCountDataName];
  };
}

/**
 * Cria o protótipo do mixin com base nos nomes passados por parametro.
 */
function createPrototype(params) {
  const {
    loadingComputedName,
    loadingCountDataName,
    startLoadingMethodName,
    endLoadingMethodName,
    clearLoadingMethodName,
    waitLoadingMethodName,
  } = params;

  return {
    data: () => ({
      [loadingCountDataName]: 0,
    }),
    methods: {
      [startLoadingMethodName]: createStartFunction(params),
      [endLoadingMethodName]: createEndFunction(params),
      [clearLoadingMethodName]: createClearFunction(params),
      [waitLoadingMethodName]: createWaitFunction(params),
    },
    computed: {
      [loadingComputedName]: createLoadingComputedFunction(params),
    },
  };
}

/**
 * Constrói um mixin de carregamento.
 *
 * @param {String} [name] Nome do carregamento para concatenar aos elementos gerados.
 *
 * Interface gerada:
 *  {
 *    data: () => ({
 *      loading{name}Count: Number,
 *    }),
 *    methods: {
 *      startLoading{name}(): void,
 *      endLoading{name}(): void,
 *      clearLoading{name}(): void,
 *      waitLoading{name}(Promise): Promise,
 *    },
 *    computed: {
 *      loading{name}: Boolean
 *    }
 *  }
 *
 * Exemplo:
 *
 * const mixin = build('Person');
 *
 * expect(mixin).toMatchObject({
 *    data: () => ({
 *      loadingPersonCount,
 *    }),
 *    methods: {
 *      startLoadingPerson,
 *      endLoadingPerson,
 *      clearLoadingPerson,
 *      waitLoadingPerson,
 *    },
 *    computed: {
 *      loadingPerson,
 *    }
 * });
 *
 * Para detalhes do funcionamento da interface gerada, veja a documentação
 * do mixin básico mais a abaixo.
 */
export function build(name = '') {
  const capitalizedName = firstCapitalized(name);
  const loadingComputedName = `loading${capitalizedName}`;
  const loadingCountDataName = `loading${capitalizedName}Count`;
  const startLoadingMethodName = `startLoading${capitalizedName}`;
  const endLoadingMethodName = `endLoading${capitalizedName}`;
  const clearLoadingMethodName = `clearLoading${capitalizedName}`;
  const waitLoadingMethodName = `waitLoading${capitalizedName}`;

  return createPrototype({
    loadingComputedName,
    loadingCountDataName,
    startLoadingMethodName,
    endLoadingMethodName,
    clearLoadingMethodName,
    waitLoadingMethodName,
  });
}

/**
 * Mixin básico de carregamento.
 *
 * Interface do mixin:
 *
 *  {
 *    data: () => ({
 *      // Contagem de carregamentos definidos.
 *      loadingCount: Number,
 *    }),
 *    methods: {
 *      // Seta um carregamento.
 *      startLoading(): void,
 *
 *      // Remove um carregamento.
 *      endLoading(): void,
 *
 *      // Limpa os carregamentos, zerando tudo.
 *      clearLoading(): void,
 *
 *      // Espera uma promessa, definindo um carregamento enquanto estiver em execução.
 *      waitLoading(Promise): Promise,
 *    },
 *    computed: {
 *      // True se a contagem for diferente de zero.
 *      loading: Boolean
 *    }
 *  }
 *
 * Exemplos de uso do mixin nos components Vue:
 *
 *  import loadingMixin from '@/services/loadingMixin';
 *
 *  export default {
 *    mixins: [
 *      loadingMixin,
 *    ],
 *    methods: {
 *      refreshDataA() {
 *        this.startLoading();
 *        Service.getData().then(() => {
 *          this.endLoading();
 *        }).catch(() => {
 *          this.endLoading();
 *        });
 *      },
 *      async refreshDataB() {
 *        this.startLoading();
 *        try {
 *          await Service.getData();
 *        } finally {
 *          this.endLoading();
 *        }
 *      },
 *      refreshDataC() {
 *        this.waitLoading(Service.getData());
 *      },
 *    }
 *  }
 *
 *  <span v-if="loading">Carregando...</span>
 */
const defaultLoadingManager = build();
export default defaultLoadingManager;
